const getAuthStatus = () => {
  if (typeof window === "undefined") return { status: "not_logged_in", uuid: "NA" };

  try {
    const userStr = localStorage.getItem("user");
    const accessToken = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");

    if (userStr && accessToken && refreshToken) {
      const parsedUser = JSON.parse(userStr);
      let uuid = parsedUser?.userData?.id || parsedUser?.id || "NA";

      if (uuid !== "NA") {
        uuid = String(uuid).substring(0, 36);
      }

      return { status: "logged_in", uuid };
    }
  } catch (error) {
    console.error("Error reading auth from localStorage", error);
  }

  return { status: "not_logged_in", uuid: "NA" };
};

const convertTimestampToYYYYMMDDHHMM = (timestamp) => {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "NA";

  return date.getFullYear() + "-" +
    String(date.getMonth() + 1).padStart(2, '0') + "-" +
    String(date.getDate()).padStart(2, '0') + " " +
    String(date.getHours()).padStart(2, '0') + ":" +
    String(date.getMinutes()).padStart(2, '0');
};

const getCommentCount = () => {
  if (typeof window !== "undefined") {
    const commentCountElement = document.querySelector(".coral-count");
    if (commentCountElement) {
      const text = commentCountElement.innerText.trim();
      const count = Number(text);
      return isNaN(count) ? "NA" : count;
    }
  }
  return "NA";
};

const sanitizeUrl = (url) => {
  if (!url || typeof url !== "string") return "NA";
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete("access_token");
    urlObj.searchParams.delete("refresh_token");
    return urlObj.toString();
  } catch (e) {
    return url
      .replace(/([?&])access_token=[^&]*/gi, "$1access_token=REDACTED")
      .replace(/([?&])refresh_token=[^&]*/gi, "$1refresh_token=REDACTED");
  }
};

let storyTimeout = null;
let lastAuthTrackTime = 0;
let lastAuthTrackUser = "";

export const pushPageDataLayer = (pageType, userData = {}, pageData = {}) => {
  if (typeof window === "undefined") return;

  if (storyTimeout) {
    clearTimeout(storyTimeout);
    storyTimeout = null;
  }

  if (pageType === "not-found" || pageType === "404") {
    const uuid = userData?.userData?.id ? String(userData.userData.id).substring(0, 36) : "NA";
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_type: "404",
      auth_status: uuid !== "NA" ? "logged_in" : "not_logged_in",
      uuid
    });
    return;
  }

  const dataLayerObject = {
    event: "page_view",
    page_type: "NA",
    author_name: "NA",
    section_name: "NA",
    published_date: "NA",
    last_updated_date: "NA",
    content_id: "NA",
    article_id: "NA",
    article_type: "NA",
    story_tags: "NA",
    story_words: "NA",
    comment_number: "NA",
    author_id: "NA",
    content_title: "NA",
    created_date: "NA",
    creator_name: "NA",
    auth_status: "NA",
    uuid: "NA",
  };

  if (pageType === "home-page" || pageType === "home") {
    dataLayerObject.page_type = "home_page";
  } else if (pageType === "story-page" || pageType === "article") {
    dataLayerObject.page_type = "article_page";

    if (pageData?.story) {
      const story = pageData.story;
      dataLayerObject.author_name = story["author-name"] || story.author || "NA";
      dataLayerObject.content_id = story.id || "NA";
      dataLayerObject.article_id = story["public-identifier"] || story["story-template"] || story.id || "NA";
      dataLayerObject.article_type = story["story-template"] || "NA";
      dataLayerObject.author_id = story.authors?.map(author => author.id).join(",") || "NA";
      dataLayerObject.content_title = story.headline || "NA";
      dataLayerObject.creator_name = story["owner-name"] || "NA";

      if (story["content-created-at"]) {
        dataLayerObject.created_date = convertTimestampToYYYYMMDDHHMM(story["content-created-at"]);
      }
      dataLayerObject.story_tags = story.tags ? story.tags.map(t => t.name).join(",") : "NA";
      dataLayerObject.story_words = story["word-count"] || "NA";

      if (story["first-published-at"] || story["published-at"]) {
        dataLayerObject.published_date = convertTimestampToYYYYMMDDHHMM(story["first-published-at"] || story["published-at"]);
      }
      if (story["last-published-at"]) {
        dataLayerObject.last_updated_date = convertTimestampToYYYYMMDDHHMM(story["last-published-at"]);
      }
      if (story.sections && story.sections.length > 0) {
        dataLayerObject.section_name = story.sections.map(s => s.name).join(",");
      }
    }
  } else if (pageType === "section-page") {
    if (pageData?.section) {
      const section = pageData.section;
      dataLayerObject.page_type = section["parent-id"] ? "sub_section_page" : "section_page";
      dataLayerObject.section_name = section["display-name"] || section.name || "NA";
    }
  } else if (pageType === "search") {
    dataLayerObject.page_type = "search_page";
  } else if (pageType === "author-page" || pageType === "author") {
    dataLayerObject.page_type = "author_page";
    if (pageData?.author) {
      dataLayerObject.author_name = pageData.author.name || "NA";
    }
  } else if (pageType === "tag-page" || pageType === "tag") {
    dataLayerObject.page_type = "tag_page";
  } else {
    dataLayerObject.page_type = pageType || "NA";
  }

  const pushToDataLayer = () => {
    const rawId = userData?.userData?.id || userData?.id;
    let uuid = rawId ? String(rawId).substring(0, 36) : "NA";
    let authStatus = uuid !== "NA" ? "logged_in" : "not_logged_in";

    if (uuid === "NA") {
      const authFromStorage = getAuthStatus();
      uuid = authFromStorage.uuid;
      authStatus = authFromStorage.status;
    }

    dataLayerObject.auth_status = authStatus;
    dataLayerObject.uuid = uuid;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(dataLayerObject);

    if (authStatus === "logged_in") {
      const skipAuthTracking = sessionStorage.getItem("skip_auth_tracking");
      if (skipAuthTracking === "true") {
        sessionStorage.removeItem("skip_auth_tracking");
      } else {
        addAuthTrackingToDataLayer(userData?.userData?.id ? userData : { userData: { id: uuid } });
      }
    }
  };

  if (pageType === "story-page" || pageType === "article") {
    storyTimeout = setTimeout(() => {
      dataLayerObject.comment_number = getCommentCount();
      pushToDataLayer();
      storyTimeout = null;
    }, 1500);
  } else {
    pushToDataLayer();
  }
};

export const pushEventToDataLayer = (eventName) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName });
};

export const addSignUpLoginToDataLayer = (url, data) => {
  if (typeof window === "undefined" || !data) return;

  const loginSource = sanitizeUrl(url);
  const userId = data?.userData?.id || data?.id || "NA";

  const marketingConsentStatus =
    data?.metadata?.receiveMarketingAndPromitionalEmail || false;

  const accountCreationDate = data?.userData?.timeJoined
    ? convertTimestampToYYYYMMDDHHMM(data.userData.timeJoined)
    : "NA";

  const loginMethod =
    data?.metadata?.login_method === "phone_login"
      ? "mobile"
      : data?.metadata?.login_method === "google_login"
        ? "google"
        : data?.metadata?.login_method === "email_login"
          ? "email"
          : "unknown";

  const createdAt = new Date(data?.userData?.timeJoined).getTime();
  const eventType =
    Date.now() - createdAt < 60000 ? "signup" : "login";

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventType,
    login_source: loginSource,
    marketing_consent_status: marketingConsentStatus,
    account_creation_date: accountCreationDate,
    user_id: userId,
    authentication_method: loginMethod
  });
};

export const addLogoutToDataLayer = (url, data) => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "logout",
    login_source: sanitizeUrl(url),
    user_id: data?.userData?.id || "NA",
    auth_status: "not_logged_in"
  });
};

export const addAuthTrackingToDataLayer = (data) => {
  if (typeof window === "undefined") return;

  const userId = data?.userData?.id || data?.id || "NA";
  const now = Date.now();
  if (userId !== "NA" && userId === lastAuthTrackUser && (now - lastAuthTrackTime < 500)) {
    return;
  }
  lastAuthTrackTime = now;
  lastAuthTrackUser = userId;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "auth",
    user_id: userId,
    auth_status: "authenticated"
  });
};

export const addClickTrackingToDataLayer = (url, text, id) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "widget_click",
    click_url: url || "NA",
    click_text: text || "NA",
    click_id: id || "NA"
  });
};
